import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Grid,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  Collapse,
  Fade,
  Zoom,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon as MuiListItemIcon
} from '@mui/material';
import {
  Group as GroupIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Computer as ComputerIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  FiberManualRecord as StatusIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import groupsApiService from '../../services/groups/GroupsApiService';

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [groupAgents, setGroupAgents] = useState({});
  const [loadingAgents, setLoadingAgents] = useState({});
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');
  
  // Menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const groupsData = await groupsApiService.getAllGroups();
      setGroups(groupsData || []);
      
      // Fetch agents for all groups immediately
      if (groupsData && groupsData.length > 0) {
        fetchAllGroupAgents(groupsData);
      }
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      showSnackbar('Failed to load groups', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllGroupAgents = async (groupsData) => {
    const agentsData = {};
    const loadingStates = {};
    
    // Fetch agents for all groups in parallel
    await Promise.all(
      groupsData.map(async (group) => {
        try {
          loadingStates[group.name] = true;
          const agents = await groupsApiService.getGroupAgents(group.name);
          agentsData[group.name] = agents || [];
        } catch (error) {
          console.error(`Failed to fetch agents for group ${group.name}:`, error);
          agentsData[group.name] = [];
        } finally {
          loadingStates[group.name] = false;
        }
      })
    );
    
    setGroupAgents(agentsData);
    setLoadingAgents(loadingStates);
  };

  const fetchGroupAgents = async (groupName) => {
    if (groupAgents[groupName]) {
      // Already fetched, just toggle
      setExpandedGroup(expandedGroup === groupName ? null : groupName);
      return;
    }

    try {
      setLoadingAgents({ ...loadingAgents, [groupName]: true });
      const agents = await groupsApiService.getGroupAgents(groupName);
      setGroupAgents({ ...groupAgents, [groupName]: agents || [] });
      setExpandedGroup(groupName);
    } catch (error) {
      console.error(`Failed to fetch agents for group ${groupName}:`, error);
      showSnackbar(`Failed to load agents for ${groupName}`, 'error');
    } finally {
      setLoadingAgents({ ...loadingAgents, [groupName]: false });
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      showSnackbar('Please enter a group name', 'warning');
      return;
    }

    try {
      setCreatingGroup(true);
      await groupsApiService.createGroup(newGroupName.trim());
      showSnackbar(`Group "${newGroupName}" created successfully`, 'success');
      setNewGroupName('');
      setCreateDialogOpen(false);
      fetchGroups();
    } catch (error) {
      console.error('Failed to create group:', error);
      showSnackbar(error.message || 'Failed to create group', 'error');
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleOpenMenu = (event, groupName) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedGroup(groupName);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSelectedGroup(null);
  };

  const handleDeleteGroup = async (groupName) => {
    handleCloseMenu();
    
    if (groupName === 'default') {
      showSnackbar('Cannot delete the default group', 'warning');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the group "${groupName}"?`)) {
      return;
    }

    try {
      await groupsApiService.deleteGroup(groupName);
      showSnackbar(`Group "${groupName}" deleted successfully`, 'success');
      fetchGroups();
      // Clear cached agents for this group
      const newGroupAgents = { ...groupAgents };
      delete newGroupAgents[groupName];
      setGroupAgents(newGroupAgents);
      if (expandedGroup === groupName) {
        setExpandedGroup(null);
      }
    } catch (error) {
      console.error('Failed to delete group:', error);
      showSnackbar(error.message || 'Failed to delete group', 'error');
    }
  };

  const handleToggleGroup = (groupName) => {
    // Simply toggle - data is already loaded
    if (expandedGroup === groupName) {
      setExpandedGroup(null);
    } else {
      setExpandedGroup(groupName);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Filter groups based on search term
  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate total agents
  const totalAgents = groups.reduce((sum, group) => sum + (group.count || 0), 0);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Fade in={true} timeout={800}>
        <Box 
          sx={{ 
            mb: 3,
            p: 2.5,
            borderRadius: 2,
            background: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #e0e0e0'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <GroupIcon sx={{ fontSize: 32, color: '#667eea' }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: '700', color: '#1a237e' }}>
                  Agent Groups
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage and organize your Wazuh agents
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Tooltip title="Refresh groups">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                  onClick={fetchGroups}
                  disabled={loading}
                  sx={{ 
                    borderColor: '#667eea',
                    color: '#667eea',
                    fontWeight: '600',
                    px: 2,
                    '&:hover': {
                      borderColor: '#5568d3',
                      background: 'rgba(102, 126, 234, 0.05)'
                    }
                  }}
                >
                  Refresh
                </Button>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                sx={{ 
                  background: '#667eea',
                  fontWeight: '600',
                  px: 2,
                  '&:hover': {
                    background: '#5568d3'
                  }
                }}
              >
                Create Group
              </Button>
            </Box>
          </Box>

          {/* Statistics Cards */}
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={4}>
              <Paper 
                sx={{ 
                  p: 1.5,
                  background: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1.5,
                  boxShadow: 'none'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box 
                    sx={{ 
                      width: 36, 
                      height: 36, 
                      borderRadius: 1.5,
                      background: '#667eea',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}
                  >
                    <GroupIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: '700', color: '#1a237e' }}>
                      {groups.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Groups
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper 
                sx={{ 
                  p: 1.5,
                  background: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1.5,
                  boxShadow: 'none'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box 
                    sx={{ 
                      width: 36, 
                      height: 36, 
                      borderRadius: 1.5,
                      background: '#667eea',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}
                  >
                    <ComputerIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: '700', color: '#1a237e' }}>
                      {totalAgents}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Agents
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper 
                sx={{ 
                  p: 1.5,
                  background: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1.5,
                  boxShadow: 'none'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box 
                    sx={{ 
                      width: 36, 
                      height: 36, 
                      borderRadius: 1.5,
                      background: '#4caf50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}
                  >
                    <StatusIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: '700', color: '#1a237e' }}>
                      {groups.length > 0 ? Math.round((totalAgents / groups.length) * 10) / 10 : 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avg per Group
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Fade>

      {/* Search Bar */}
      <Fade in={true} timeout={1000}>
        <Paper 
          sx={{ 
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            border: '1px solid #e0e0e0',
            background: 'white'
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Search groups..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: '#667eea', fontSize: 20 }} />,
              sx: {
                '& fieldset': { border: 'none' }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.95rem'
              }
            }}
          />
        </Paper>
      </Fade>

      {/* Groups List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : groups.length === 0 ? (
        <Paper 
          sx={{ 
            p: 6, 
            textAlign: 'center',
            borderRadius: 3,
            border: '2px dashed #e0e0e0'
          }}
        >
          <GroupIcon sx={{ fontSize: 80, color: '#bdbdbd', mb: 2 }} />
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No Groups Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Create your first group to organize your agents
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              px: 4,
              py: 1.5
            }}
          >
            Create Group
          </Button>
        </Paper>
      ) : filteredGroups.length === 0 ? (
        <Paper 
          sx={{ 
            p: 6, 
            textAlign: 'center',
            borderRadius: 4,
            border: '2px dashed #e0e0e0',
            background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)'
          }}
        >
          <SearchIcon sx={{ fontSize: 80, color: '#bdbdbd', mb: 2 }} />
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No Groups Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            No groups match your search criteria
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setSearchTerm('')}
            sx={{ 
              borderColor: '#667eea', 
              color: '#667eea',
              borderRadius: 3,
              px: 4,
              py: 1
            }}
          >
            Clear Search
          </Button>
        </Paper>
      ) : (
        <Paper 
          sx={{ 
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #e0e0e0',
            background: 'white'
          }}
        >
          <List sx={{ p: 0 }}>
            {filteredGroups.map((group, index) => (
              <React.Fragment key={group.name}>
                {index > 0 && <Divider />}
                <Fade in={true} timeout={300 + (index * 50)}>
                  <Box>
                    <ListItem
                      onClick={() => handleToggleGroup(group.name)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        cursor: 'pointer',
                        background: expandedGroup === group.name
                          ? '#667eea'
                          : 'white',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: expandedGroup === group.name
                            ? '#5568d3'
                            : '#f5f5f5'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 44 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            background: expandedGroup === group.name
                              ? 'rgba(255, 255, 255, 0.2)'
                              : '#667eea',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <GroupIcon sx={{ fontSize: 22 }} />
                        </Box>
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: '600',
                                color: expandedGroup === group.name ? 'white' : '#1a237e',
                                fontSize: '0.95rem'
                              }}
                            >
                              {group.name}
                            </Typography>
                            {group.name === 'default' && (
                              <Chip
                                label="Default"
                                size="small"
                                sx={{
                                  background: expandedGroup === group.name
                                    ? 'rgba(255, 255, 255, 0.25)'
                                    : '#4caf50',
                                  color: 'white',
                                  fontWeight: '600',
                                  fontSize: '0.65rem',
                                  height: 20
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Chip
                            icon={<ComputerIcon sx={{ fontSize: 14 }} />}
                            label={`${group.count || 0} agents`}
                            size="small"
                            sx={{
                              background: expandedGroup === group.name
                                ? 'rgba(255, 255, 255, 0.2)'
                                : '#f5f5f5',
                              color: expandedGroup === group.name ? 'white' : '#667eea',
                              fontWeight: '600',
                              fontSize: '0.75rem',
                              height: 22,
                              borderRadius: 1,
                              mt: 0.5,
                              '& .MuiChip-icon': {
                                color: expandedGroup === group.name ? 'white' : '#667eea'
                              }
                            }}
                          />
                        }
                      />

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {group.name !== 'default' && (
                          <Tooltip title="More options">
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenMenu(e, group.name)}
                              sx={{
                                color: expandedGroup === group.name ? 'white' : '#666',
                                '&:hover': {
                                  background: expandedGroup === group.name
                                    ? 'rgba(255, 255, 255, 0.15)'
                                    : 'rgba(0, 0, 0, 0.05)'
                                }
                              }}
                            >
                              <MoreVertIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          sx={{
                            color: expandedGroup === group.name ? 'white' : '#667eea'
                          }}
                        >
                          {expandedGroup === group.name ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
                        </IconButton>
                      </Box>
                    </ListItem>

                    {/* Agents Dropdown */}
                    <Collapse in={expandedGroup === group.name} timeout="auto" unmountOnExit>
                      <Box sx={{ background: '#f9f9f9', p: 2 }}>
                        {groupAgents[group.name]?.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 3 }}>
                            <InfoIcon sx={{ fontSize: 36, color: '#bdbdbd', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              No agents in this group
                            </Typography>
                          </Box>
                        ) : (
                          <List sx={{ p: 0 }}>
                            {groupAgents[group.name]?.map((agent, agentIndex) => (
                              <Fade in={true} timeout={200 + (agentIndex * 80)} key={agent.id}>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    mb: 1,
                                    borderRadius: 1.5,
                                    overflow: 'hidden',
                                    border: '1px solid #e0e0e0',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)',
                                      border: '1px solid #667eea'
                                    }
                                  }}
                                >
                                  <ListItem
                                    sx={{
                                      py: 1.5,
                                      px: 2,
                                      background: 'white'
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                      <Box
                                        sx={{
                                          width: 36,
                                          height: 36,
                                          borderRadius: 1.5,
                                          background: '#667eea',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: 'white'
                                        }}
                                      >
                                        <ComputerIcon sx={{ fontSize: 20 }} />
                                      </Box>
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={
                                        <Typography variant="body1" sx={{ fontWeight: '600', color: '#1a237e', mb: 0.5, fontSize: '0.9rem' }}>
                                          {agent.name}
                                        </Typography>
                                      }
                                      secondary={
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                          <Chip
                                            label={`ID: ${agent.id}`}
                                            size="small"
                                            sx={{
                                              height: 20,
                                              fontSize: '0.7rem',
                                              background: '#e3f2fd',
                                              color: '#1976d2',
                                              fontWeight: '600',
                                              borderRadius: 1
                                            }}
                                          />
                                          <Chip
                                            label={`IP: ${agent.ip || 'N/A'}`}
                                            size="small"
                                            sx={{
                                              height: 20,
                                              fontSize: '0.7rem',
                                              background: '#f3e5f5',
                                              color: '#7b1fa2',
                                              fontWeight: '600',
                                              borderRadius: 1
                                            }}
                                          />
                                        </Box>
                                      }
                                    />
                                    <Tooltip title={agent.status === 'active' ? 'Agent is active' : 'Agent is inactive'}>
                                      <Chip
                                        icon={<StatusIcon sx={{ fontSize: 10 }} />}
                                        label={agent.status === 'active' ? 'Active' : agent.status || 'Unknown'}
                                        size="small"
                                        sx={{
                                          background: agent.status === 'active'
                                            ? '#4caf50'
                                            : '#9e9e9e',
                                          color: 'white',
                                          fontWeight: '600',
                                          fontSize: '0.7rem',
                                          height: 24,
                                          borderRadius: 1.5,
                                          '& .MuiChip-icon': {
                                            color: 'white',
                                            marginLeft: '6px'
                                          }
                                        }}
                                      />
                                    </Tooltip>
                                  </ListItem>
                                </Paper>
                              </Fade>
                            ))}
                          </List>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                </Fade>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* More Options Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: 160
          }
        }}
      >
        <MenuItem 
          onClick={() => handleDeleteGroup(selectedGroup)}
          sx={{
            color: '#f44336',
            '&:hover': {
              background: 'rgba(244, 67, 54, 0.08)'
            }
          }}
        >
          <MuiListItemIcon>
            <DeleteIcon sx={{ fontSize: 20, color: '#f44336' }} />
          </MuiListItemIcon>
          <Typography variant="body2">Delete Group</Typography>
        </MenuItem>
      </Menu>

      {/* Create Group Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => !creatingGroup && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            color: 'white',
            p: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <GroupIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: '700', mb: 0.5 }}>
                Create New Group
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Add a new agent group to your organization
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 3, px: 3 }}>
          <TextField
            autoFocus
            label="Group Name"
            fullWidth
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="e.g., production, webservers, database"
            disabled={creatingGroup}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !creatingGroup) {
                handleCreateGroup();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                  borderWidth: 2
                }
              }
            }}
          />
          <Alert 
            severity="info" 
            sx={{ 
              mt: 3,
              borderRadius: 2,
              border: '1px solid #e3f2fd',
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: '600', mb: 0.5 }}>
              💡 Tips for naming groups:
            </Typography>
            <Typography variant="body2">
              • Use descriptive names (production, staging, development)<br/>
              • Keep names lowercase and use hyphens for spaces<br/>
              • Group names must be unique
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setCreateDialogOpen(false)} 
            disabled={creatingGroup}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateGroup}
            variant="contained"
            disabled={creatingGroup || !newGroupName.trim()}
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              minWidth: 120,
              px: 4,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: '700',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)'
              },
              '&:disabled': {
                background: '#e0e0e0'
              }
            }}
          >
            {creatingGroup ? <CircularProgress size={24} color="inherit" /> : 'Create Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Groups;
